/**
 * localStorage-backed storage for Quick View variant selections.
 * Selections are keyed by productId (variation group ID) and expire after 30 minutes.
 * The same storage is read by the PDP to pre-populate selections when the user navigates
 * from a Quick View to the product detail page.
 */

const STORAGE_KEY_PREFIX = 'qv_sel_'
const TTL_MS = 30 * 60 * 1000 // 30 minutes

const isAvailable = () => typeof window !== 'undefined' && Boolean(window.localStorage)

/**
 * Persist variant selections for a product.
 * @param {string} productId
 * @param {Object} selections  e.g. { size: 'M', length: '30' }
 */
export const setQVSelections = (productId, selections) => {
    if (!productId || !isAvailable()) return
    if (!selections || Object.keys(selections).length === 0) {
        clearQVSelections(productId)
        return
    }
    try {
        localStorage.setItem(
            STORAGE_KEY_PREFIX + productId,
            JSON.stringify({selections, ts: Date.now()})
        )
    } catch (_) {
        // Ignore QuotaExceededError or SecurityError
    }
}

/**
 * Retrieve stored selections for a product. Returns {} if missing or expired.
 * @param {string} productId
 * @returns {Object}
 */
export const getQVSelections = (productId) => {
    if (!productId || !isAvailable()) return {}
    try {
        const raw = localStorage.getItem(STORAGE_KEY_PREFIX + productId)
        if (!raw) return {}
        const {selections, ts} = JSON.parse(raw)
        if (!selections || Date.now() - ts > TTL_MS) {
            localStorage.removeItem(STORAGE_KEY_PREFIX + productId)
            return {}
        }
        return selections
    } catch (_) {
        return {}
    }
}

/**
 * Remove stored selections for a product.
 * @param {string} productId
 */
export const clearQVSelections = (productId) => {
    if (!productId || !isAvailable()) return
    try {
        localStorage.removeItem(STORAGE_KEY_PREFIX + productId)
    } catch (_) {}
}
